import { apiClient } from '../apiClient';

const getTokenHeaders = (extraHeaders = {}) => {
  const token = localStorage.getItem('token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
};

// Enriched, read-only list of vxtConfigurations for the Settings page:
// { defaultConfigurationId, items: [{ ...config, branchName, deviceCount, isDefault, useTarUpdater }] }
export const getDeviceConfigsOverview = async () => {
  try {
    const response = await apiClient.get('/vxtConfigurations/overview', {
      params: { _ts: Date.now() },
      headers: getTokenHeaders({ 'Cache-Control': 'no-cache', Pragma: 'no-cache' }),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching device configs overview:', error);
    throw error;
  }
};

// Update a non-default vxtConfiguration. The backend rejects edits to the locked Default config.
// Clone a config's SETTINGS into a new config (no devices are assigned).
export const cloneDeviceConfig = async (id, name = '') => {
  const response = await apiClient.post(`/vxtConfigurations/${id}/clone`, { name }, { headers: getTokenHeaders() });
  return response.data;
};

export const updateDeviceConfig = async (id, payload) => {
  const response = await apiClient.put(`/vxtConfigurations/${id}`, payload, { headers: getTokenHeaders() });
  return response.data;
};

// Devices currently assigned to a config: { total, limit, items: [{ sn, name, city, ... }], serials }.
// Optional city filter → every device of that city on the config (bypasses the display limit).
export const getConfigDevices = async (id, { limit = 200, city = '', search = '' } = {}) => {
  const params = { limit, _ts: Date.now() };
  if (city) params.city = city;
  if (search) params.search = search;
  const response = await apiClient.get(`/vxtConfigurations/${id}/devices`, {
    params,
    headers: getTokenHeaders({ 'Cache-Control': 'no-cache', Pragma: 'no-cache' }),
  });
  return response.data;
};

// Batch-assign a config to many serials (RBPI only; non-RBPI skipped server-side).
// Returns { assignedCount, assigned, skipped: [{serialNumber,type}], failed }.
export const assignDevicesBatch = async (configId, serials) => {
  const response = await apiClient.post(
    `/vxtConfigurations/${configId}/assign-devices`,
    { serials },
    { headers: getTokenHeaders() },
  );
  return response.data;
};

export const getDeviceConfigById = async (id) => {
  try {
    const response = await apiClient.get(`/vxtConfigurations/${id}`, {
      headers: getTokenHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching device config by id:', error);
    throw error;
  }
};

// Assign a vxtConfiguration to a single device by serial number (the manual canary lever).
// Reverting to Default = passing the default config _id here.
export const assignDeviceConfig = async (serialNumber, vxtConfigurationId) => {
  try {
    const response = await apiClient.post(
      '/devices/updateConfig',
      { sn: serialNumber, vxtConfiguration: vxtConfigurationId },
      { headers: getTokenHeaders() },
    );
    return response.data;
  } catch (error) {
    console.error('Error assigning device config:', error);
    throw error;
  }
};

// EXCEPTION push to a small explicit list of serials (NOT a rollout). Backend caps the count and
// rejects anything that isn't an explicit serial array. action ∈ updateFw|updatePlaylist|restartMainProcess.
export const pushFleetCommand = async ({ serials, action, jitterSeconds = 0 }) => {
  const response = await apiClient.post(
    '/fleet-push/command',
    { serials, action, jitterSeconds },
    { headers: getTokenHeaders() },
  );
  return response.data;
};

// --- Reference lists that back the config dropdowns (branches / server addresses / mqtt) ---

export const getConfigBranches = async () => {
  const response = await apiClient.get('/branches', { headers: getTokenHeaders() });
  return response.data;
};

export const createConfigBranch = async (payload) => {
  const response = await apiClient.post('/branches/create', payload, { headers: getTokenHeaders() });
  return response.data;
};

export const getConfigServerAddresses = async () => {
  const response = await apiClient.get('/serverAddress', { headers: getTokenHeaders() });
  return response.data;
};

export const createConfigServerAddress = async (payload) => {
  const response = await apiClient.post('/serverAddress/create', payload, { headers: getTokenHeaders() });
  return response.data;
};

export const getConfigMqttBrokers = async () => {
  const response = await apiClient.get('/mqtt', { headers: getTokenHeaders() });
  return response.data;
};

export const createConfigMqttBroker = async (payload) => {
  const response = await apiClient.post('/mqtt/create', payload, { headers: getTokenHeaders() });
  return response.data;
};
